import { z } from "zod";
import { IdempotencyKeySchema, IdSchema, IsoDateTimeSchema, Sha256Schema } from "./primitives.js";
// The suffix is part of the transmitted service code; diagnosis codes are a separate catalogue.
export const OhipServiceCodeSchema = z.string().regex(/^[A-Z][0-9]{3}[ABC]$/);
export const OhipAssessmentCodeSchema = z.union([z.literal(""), OhipServiceCodeSchema]);
export const OhipServiceLineSchema = z
    .object({
    code: OhipServiceCodeSchema,
    units: z.number().int().min(1).max(99),
})
    .strict();
export const OhipClaimStatusSchema = z.enum(["needs_review", "on_hold", "ready"]);
export const OhipBillingFieldsSchema = z
    .object({
    assessmentCode: OhipAssessmentCodeSchema,
    additionalServices: z.array(OhipServiceLineSchema).max(19).default([]),
    serviceUnits: z.number().int().min(1).max(99).default(1),
    diagnosticCode: z.string().regex(/^(?:[0-9]{3})?$/),
    billingNumber: z.string().regex(/^(?:[0-9]{6})?$/),
    groupNumber: z.string().regex(/^[A-Z0-9]{4}$/),
    healthNumber: z.string().regex(/^(?:[0-9]{10})?$/),
    versionCode: z.string().regex(/^[A-Z]{0,2}$/),
    officeFfsConfirmed: z.boolean(),
    documentationReviewed: z.boolean(),
    coverageReviewed: z.boolean(),
    reviewNote: z.string().max(1000),
})
    .strict();
// ClinicAid (EXECDEV-65). A claim approved at a connected clinic is handed to ClinicAid;
// these states track it until OHIP pays it. needs_correction, send_failed and removed
// return the claim to review; the other states lock it.
export const OhipSubmissionStateSchema = z.enum([
    "queued",
    "sent",
    "needs_correction",
    "send_failed",
    "accepted",
    "paid",
    "payment_issue",
    "removed",
]);
export const OhipVendorMessageSchema = z
    .object({
    source: z.enum(["clinicaid", "ohip"]),
    text: z.string().min(1).max(1000),
    code: z.string().max(40).nullable().default(null),
    at: z.string().max(40).nullable().default(null),
})
    .strict();
export const OhipClaimSubmissionSummarySchema = z
    .object({
    state: OhipSubmissionStateSchema,
    vendorStatus: z.string().nullable(),
    paidCents: z.number().int().nullable(),
})
    .strict();
export const OhipClaimSubmissionSchema = OhipClaimSubmissionSummarySchema.extend({
    invoiceId: IdSchema.nullable(),
    messages: z.array(OhipVendorMessageSchema),
    paymentDate: z.string().date().nullable(),
    submittedAt: IsoDateTimeSchema.nullable(),
    syncedAt: IsoDateTimeSchema.nullable(),
}).strict();
export const OhipProvincialRecordSchema = z
    .object({
    firstName: z.string().nullable(),
    middleName: z.string().nullable(),
    lastName: z.string().nullable(),
    gender: z.string().nullable(),
    birthDate: z.string().nullable(),
})
    .partial()
    .strict();
export const OhipEligibilitySchema = z
    .object({
    status: z.enum(["not_checked", "pending", "checked", "failed"]),
    eligible: z.boolean().nullable(),
    message: z.string(),
    checkedAt: IsoDateTimeSchema.nullable(),
    provincial: OhipProvincialRecordSchema.nullable(),
    birthDateMatches: z.boolean().nullable(),
})
    .strict();
// "unavailable": the clinic switched ClinicAid on, but this API has no ClinicAid credentials.
export const OhipConnectionSchema = z.enum(["connected", "not_connected", "unavailable"]);
export const OHIP_SERVICE_LOCATION_INDICATORS = [
    "OFF",
    "HDS",
    "HED",
    "HIP",
    "HOP",
    "HRP",
    "IHF",
    "OTN",
    "PDF",
    "RTF",
];
export const OhipBillingSettingsFieldsSchema = z
    .object({
    clinicaidEnabled: z.boolean(),
    serviceLocationIndicator: z.enum(OHIP_SERVICE_LOCATION_INDICATORS),
    masterNumber: z.string().regex(/^(?:[0-9]{4})?$/),
    payee: z.enum(["P", "S"]),
    specialtyCode: z.string().regex(/^[0-9]{2}$/),
})
    .strict();
export const OhipBillingSettingsSchema = OhipBillingSettingsFieldsSchema.extend({
    siteId: IdSchema,
    version: z.number().int().nonnegative(),
    canEdit: z.boolean(),
    connection: OhipConnectionSchema.default("not_connected"),
}).strict();
export const SaveOhipBillingSettingsSchema = z
    .object({
    expectedVersion: z.number().int().nonnegative(),
    idempotencyKey: IdempotencyKeySchema,
    settings: OhipBillingSettingsFieldsSchema,
})
    .strict();
export const OhipClaimSummarySchema = z
    .object({
    id: IdSchema,
    siteId: IdSchema,
    patientId: IdSchema,
    encounterId: IdSchema,
    patientName: z.string(),
    physicianId: IdSchema,
    physicianName: z.string(),
    serviceDate: z.string().date(),
    version: z.number().int().positive(),
    status: OhipClaimStatusSchema,
    assessmentCode: OhipAssessmentCodeSchema,
    diagnosticCode: z.string(),
    amountCents: z.number().int().nonnegative().nullable(),
    blockers: z.array(z.string()),
    reviewContextHash: Sha256Schema,
    updatedAt: IsoDateTimeSchema,
    canEdit: z.boolean(),
    canApprove: z.boolean(),
    submission: OhipClaimSubmissionSummarySchema.nullable().default(null),
})
    .strict();
export const OhipClaimDetailSchema = OhipClaimSummarySchema.extend({
    submission: OhipClaimSubmissionSchema.nullable().default(null),
    eligibility: OhipEligibilitySchema.nullable().default(null),
    connection: OhipConnectionSchema.default("not_connected"),
    fields: OhipBillingFieldsSchema,
    dateOfBirth: z.string().date(),
    noteId: IdSchema,
    noteVersion: z.number().int().positive(),
    noteSections: z.record(z.string(), z.unknown()),
    feeSchedule: z.object({ version: z.string(), sourceUrl: z.string().url() }).nullable(),
    agent: z
        .object({
        status: z.enum(["pending", "running", "completed", "failed", "superseded"]),
        recommendation: z.unknown().nullable(),
    })
        .nullable()
        .default(null),
    history: z.array(z.object({
        version: z.number().int().positive(),
        action: z.string(),
        actorName: z.string(),
        recordedAt: IsoDateTimeSchema,
    })),
}).strict();
// Billing tabs (EXECDEV-117). A claim ClinicAid holds is Submitted (sending, sent, accepted
// or with a payment issue) until OHIP pays it, then Paid.
export const OhipBillingTabSchema = z.enum([
    "all",
    "needs_review",
    "on_hold",
    "ready",
    "submitted",
    "paid",
]);
const tabCount = z.number().int().nonnegative();
export const OhipBillingCountsSchema = z
    .object({
    all: tabCount,
    needs_review: tabCount,
    on_hold: tabCount,
    ready: tabCount,
    submitted: tabCount,
    paid: tabCount,
})
    .strict();
export const OhipBillingQueueSchema = z
    .object({
    siteId: IdSchema,
    items: z.array(OhipClaimSummarySchema),
    total: z.number().int().nonnegative(),
    offset: z.number().int().nonnegative(),
    limit: z.number().int().positive(),
    connection: OhipConnectionSchema,
    submissionEnabled: z.boolean(),
    // Claims per tab for the current search and service date.
    counts: OhipBillingCountsSchema.optional(),
})
    .strict();
export const OhipBillingFilterSchema = z
    .object({
    search: z.string().trim().max(120).default(""),
    // "sent" (Submitted and Paid together) is still accepted from clients built before the split.
    status: z.enum([...OhipBillingTabSchema.options, "sent"]).default("all"),
    serviceDate: z.union([z.literal(""), z.string().date()]).default(""),
})
    .strict();
export const OhipClaimChangeSchema = z.discriminatedUnion("action", [
    z
        .object({
        action: z.literal("save"),
        fields: OhipBillingFieldsSchema,
        expectedVersion: z.number().int().positive(),
        reviewContextHash: Sha256Schema,
        idempotencyKey: IdempotencyKeySchema,
    })
        .strict(),
    z
        .object({
        action: z.enum(["approve", "hold", "reopen"]),
        expectedVersion: z.number().int().positive(),
        reviewContextHash: Sha256Schema,
        idempotencyKey: IdempotencyKeySchema,
    })
        .strict(),
]);
export const DeleteOhipClaimSchema = z
    .object({
    expectedVersion: z.number().int().positive(),
    reviewContextHash: Sha256Schema,
    idempotencyKey: IdempotencyKeySchema,
})
    .strict();
export const OhipClaimDeletionSchema = z
    .object({
    id: IdSchema,
    siteId: IdSchema,
    encounterId: IdSchema,
    deleted: z.literal(true),
})
    .strict();
export const EncounterBillingDiagnosisSchema = z
    .object({
    encounterId: IdSchema,
    siteId: IdSchema,
    version: z.number().int().nonnegative(),
    code: z.string(),
    description: z.string(),
    claimId: IdSchema.nullable(),
    claimStatus: OhipClaimStatusSchema.nullable().default(null),
    signed: z.boolean(),
    canEdit: z.boolean(),
    source: z.enum(["manual", "agent"]).default("manual"),
    inputHash: Sha256Schema.nullable().default(null),
    provenance: z.unknown().nullable().default(null),
})
    .strict();
export const SaveEncounterBillingDiagnosisSchema = z
    .object({
    expectedVersion: z.number().int().nonnegative(),
    code: z.string().regex(/^(?:[0-9]{3})?$/),
    idempotencyKey: IdempotencyKeySchema,
    suggestion: z
        .object({
        inputHash: Sha256Schema,
        model: z.string().min(1).max(120),
        promptVersion: z.string().min(1).max(120),
        rationale: z.string().max(1200),
    })
        .strict()
        .optional(),
})
    .strict();
//# sourceMappingURL=ohip-billing.js.map